import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

/**
 * Handles the redirect from Google OAuth consent screen.
 * Google redirects here with ?code=AUTH_CODE&state=SERVICE_NAME
 * We exchange the code for tokens via the backend.
 */
export default function GoogleOAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const rawState = searchParams.get('state') || '';
      const error = searchParams.get('error');

      // State format: "service" or "service|from_page" (e.g. "ga4|integrations")
      const stateParts = rawState.split('|');
      const stateService = stateParts[0];
      const fromState = stateParts[1] || searchParams.get('from') || '';

      // Determine where to redirect after processing
      const returnPath = ['integrations', 'settings'].includes(fromState)
        ? '/dashboard/integrations'
        : '/onboarding';

      if (error) {
        toast.error(`Google authorization failed: ${error}`);
        navigate(returnPath, { replace: true });
        return;
      }

      if (!code) {
        toast.error('No authorization code received from Google');
        navigate(returnPath, { replace: true });
        return;
      }

      const service = stateService || 'ga4';

      try {
        // The redirect_uri MUST match exactly what was sent in the auth request
        const redirectUri = window.location.origin + '/oauth/google/callback';

        await api.post('/integrations/google/exchange-code', {
          code: code,
          service: service,
          redirect_uri: redirectUri,
        });

        toast.success(`${service.toUpperCase()} connected successfully with API access!`);

        // Clean URL and redirect
        window.history.replaceState(null, '', returnPath);
        navigate(returnPath, { replace: true });
      } catch (err) {
        console.error('Token exchange error:', err);
        const detail = err.response?.data?.detail || 'Failed to connect';
        toast.error(detail);
        navigate(returnPath, { replace: true });
      }
    };

    processCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
        <p className="text-slate-600">Connecting your Google Analytics...</p>
      </div>
    </div>
  );
}
